import React, { useEffect, useRef, useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  useTheme,
  Card,
  CardContent,
  Button,
  LinearProgress,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useApiClient } from "../api";
import { alpha } from "@mui/material/styles";
import FruitifyIcon from "./FruitifyIcons";

const JobProgress = ({ job, onCancel }) => {
  const { message, progress } = job || {};
  const theme = useTheme();
  return (
    <Card
      sx={{
        backgroundColor: alpha(theme.palette.info.light, 0.5),
        mb: 2,
      }}
    >
      <CardContent>
        {/* Top row: icon + title, cancel button */}
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <FruitifyIcon // TODO: allow customisation depending on job (perhaps look up dict depending on type)
              name="Assignment"
              fontSize="medium"
              sx={{ color: theme.palette.text.primary }}
            />
            {job.jobType?.jobType && (
              <Typography variant="body1">{job.jobType.jobType}</Typography>
            )}
          </Box>
          {onCancel && ( // TODO: implement - for now hide as not providing onCancel
            <Button
              onClick={onCancel}
              variant={"card-action"}
              sx={{ backgroundColor: "transparent" }}
            >
              Cancel
            </Button>
          )}
        </Box>

        {/* Extraction message */}
        {message && (
          <Typography variant="body2" sx={{ mb: 1, ml: 4 }}>
            {message}
          </Typography>
        )}

        {/* Progress bar */}
        <LinearProgress
          variant="determinate"
          value={progress ?? 0}
          sx={{
            height: 8,
            borderRadius: 2,
            backgroundColor: alpha(theme.palette.background.paper, 0.3),
          }}
        />
      </CardContent>
    </Card>
  );
};

const SingleJobWatcher = ({ job, onUpdate }) => {
  const ApiClient = useApiClient();
  const prevJobRef = useRef(job);

  useEffect(() => {
    if (
      !job ||
      !["pending", "in_progress", "in progress"].includes(
        job.status?.toLowerCase(),
      )
    )
      return;

    let cancelled = false;

    const poll = async () => {
      try {
        const updated = await ApiClient.core.getJobStatus({ jobId: job.id });
        if (!cancelled) {
          const prevJob = prevJobRef.current;

          // Only call onUpdate if key fields changed
          if (
            updated.status !== prevJob.status ||
            updated.progress !== prevJob.progress ||
            updated.message !== prevJob.message
          ) {
            prevJobRef.current = updated;
            onUpdate(updated);
          }
        }
      } catch (e) {
        console.error("Job polling failed", e);
      }
    };

    poll();
    const interval = setInterval(poll, 5000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [job?.id, job?.status]);

  return null;
};

const JobsAccordion = ({ jobs = [], onRefresh, onJobUpdate }) => {
  const [jobsState, setJobsState] = useState([]);

  // initialise from props
  useEffect(() => {
    const map = {};
    jobs.forEach((j) => (map[j.id] = j));
    setJobsState(map);
  }, [jobs]);

  const handleUpdate = (updated) => {
    setJobsState((prev) => {
      const newState = { ...prev, [updated.id]: updated };
      return newState;
    });

    // Always refresh whenever job updates
    onJobUpdate?.(updated);
    onRefresh?.();
  };

  const pendingJobs = Object.values(jobsState).filter((j) =>
    ["pending", "in_progress", "in progress"].includes(j.status?.toLowerCase()),
  );

  if (!pendingJobs.length) return null;

  return (
    <>
      {/* polling watchers */}
      {pendingJobs.map((job) => (
        <SingleJobWatcher key={job.id} job={job} onUpdate={handleUpdate} />
      ))}

      {/* UI */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>Jobs in progress ({pendingJobs.length})</Typography>
        </AccordionSummary>

        <AccordionDetails>
          <Box>
            {pendingJobs.map((job) => (
              <JobProgress key={job.id} job={job} />
            ))}
          </Box>
        </AccordionDetails>
      </Accordion>
    </>
  );
};

export { SingleJobWatcher, JobsAccordion };
